/**
 * Parent Window Communication Utility
 *
 * Sends postMessage notifications to parent window when running in an iframe.
 * Used for auth status updates and ticket events.
 */

// Allowed parent origins for postMessage communication.
// Must cover every region Izzi is served from - a missing origin silently
// breaks the auth handshake for that region (see #4625).
const getAllowedOrigins = () => {
  return [
    'https://go-app-amer-dev.wefitec.com',
    'https://go-app-amer-stage.wefitec.com',
    'https://go-app-amer.wefitec.com',
    'https://go-app-emea.wefitec.com',
    'https://go-app-apac.wefitec.com',
  ]
}

/**
 * Check if we're running inside an iframe
 */
export const isInIframe = () => {
  try {
    return window.self !== window.top
  } catch (e) {
    // If we can't access window.top due to cross-origin, we're in an iframe
    return true
  }
}

/**
 * Send a message to the parent window
 * Only sends if we're in an iframe and to allowed origins
 *
 * @param {Object} message - The message object to send
 */
export const notifyParent = (message) => {
  // Only send if we're in an iframe
  if (!isInIframe()) {
    return
  }

  const allowedOrigins = getAllowedOrigins()

  // Send to all allowed origins (parent will validate on their end too)
  allowedOrigins.forEach((origin) => {
    try {
      window.parent.postMessage(message, origin)
    } catch (error) {
      console.warn(`Failed to send postMessage to ${origin}:`, error)
    }
  })
}

// Auth status messages
export const notifyAuthSuccess = (account) => {
  notifyParent({
    type: 'AUTH_SUCCESS',
    account: account ? {
      username: account.username,
      name: account.name,
    } : undefined,
    timestamp: new Date().toISOString(),
  })
}

export const notifyAuthFailed = (error) => {
  notifyParent({
    type: 'AUTH_FAILED',
    error: String(error),
    timestamp: new Date().toISOString(),
  })
}

// Ticket event messages
export const notifyTicketUpdated = (ticketStatus, ticketId) => {
  notifyParent({
    type: 'TICKET_UPDATED',
    ticketId,
    ticketStatus,
    timestamp: new Date().toISOString(),
  })
}

export const notifyTicketResolved = (ticketId, resolution) => {
  notifyParent({
    type: 'TICKET_RESOLVED',
    ticketId,
    resolution,
    timestamp: new Date().toISOString(),
  })
}

// Dispute event messages
export const notifyDisputeCreated = (disputeId) => {
  notifyParent({
    type: 'DISPUTE_CREATED',
    dispute_id: disputeId,
    timestamp: new Date().toISOString(),
  })
}

export const notifyDisputesCreated = (disputeIds) => {
  notifyParent({
    type: 'DISPUTES_CREATED',
    dispute_ids: disputeIds,
    timestamp: new Date().toISOString(),
  })
}

// Auth request message - asks parent to provide access token
export const notifyAuthRequired = () => {
  console.log('[AgentFlow] Sending AUTH_REQUIRED to parent')
  notifyParent({
    type: 'AUTH_REQUIRED',
    timestamp: new Date().toISOString(),
  })
}

// Parent token refresh - requests a new token from parent and waits for response
let parentTokenRefreshPromise = null

/**
 * Request a fresh token from the parent app.
 * Clears the stale token, sends AUTH_REQUIRED, waits for AUTH_TOKEN response.
 * Deduplicates concurrent calls (multiple 401s share one refresh).
 */
export const refreshParentToken = (timeoutMs = 10000) => {
  if (parentTokenRefreshPromise) return parentTokenRefreshPromise

  parentTokenRefreshPromise = new Promise((resolve, reject) => {
    const allowedOrigins = getAllowedOrigins()

    const timeout = setTimeout(() => {
      window.removeEventListener('message', handler)
      parentTokenRefreshPromise = null
      reject(new Error('Parent token refresh timeout'))
    }, timeoutMs)

    const handler = (event) => {
      if (!allowedOrigins.includes(event.origin)) return
      if (event.data?.type === 'AUTH_TOKEN' && event.data?.accessToken) {
        console.log('[AgentFlow] Received AUTH_TOKEN from parent')
        clearTimeout(timeout)
        window.removeEventListener('message', handler)
        sessionStorage.setItem('agentflow_parent_token', event.data.accessToken)
        parentTokenRefreshPromise = null
        resolve(event.data.accessToken)
      }
    }

    window.addEventListener('message', handler)
    sessionStorage.removeItem('agentflow_parent_token')
    notifyAuthRequired()
  })

  return parentTokenRefreshPromise
}
